import useBreakpoints from "core/hooks/useBreakpoints";
import ChecklistIcon from "core/icons/checklist/checklist.svg";
import { checkAvailabilityById, toggleElementsById } from "core/utils/arrayHelper";
import useOutsideClickHandler from "core/utils/useOutsideClickHandler";
import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  forwardRef,
  ReactNode,
  CSSProperties,
} from "react";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import {
  defaultCountries,
  parseCountry,
  FlagImage,
  ParsedCountry,
  CountryIso2,
} from "react-international-phone";
import mergeRefs from "react-merge-refs";
import { ThemeProvider, useTheme } from "styled-components";

import { Button, TextBold, Checkbox, FlexContainer, Divider, Text, Icon } from "ui";

import {
  Container,
  InputStyle,
  ErrorMessage,
  Wrapper,
  SideButton,
  FormatCurrency,
  SearchResultContainer,
  ButtonWrapper,
  SearchList,
  EmptyDataSmile,
  CrossIcon,
  Dropdown,
  CountryCodeContainer,
} from "./_textFieldStyle";

export type TextFieldProps = {
  value?: string | number;
  variant?: "currency" | "phone_number" | "default";
  type?: string;
  width?: string;
  height?: string;
  name?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  spellCheck?: boolean;
  error?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  errorPosition?: "left" | "right";
  search?: boolean;
  searchPosition?: "start" | "end";
  prefixContent?: React.ReactNode;
  onClickButton?: (...args: any[]) => any;
  withSideButton?: boolean;
  withClearButton?: boolean;
  buttonText?: string;
  options?: any[];
  onApply?: (...args: any[]) => any;
  onReset?: (...args: any[]) => any;
  searchBoxTitle?: string;
  infiniteScrollConfig?: any;
  onClickOptions?: (...args: any[]) => any;
  onClearInput?: () => void;
  onFocus?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  isMultiple?: boolean;
  "data-testid"?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  icon?: ReactNode;
  rightIcon?: ReactNode;
  inputStyle?: CSSProperties;
  containerStyle?: CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  phoneNumberProps?: {
    country: ParsedCountry;
    setCountry: (
      countryIso2: CountryIso2,
      options?: {
        focusOnInput: boolean;
      }
    ) => void;
  };
  className?: string;
};

const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    width,
    height,
    prefixContent,
    type,
    name,
    placeholder,
    onChange,
    error,
    spellCheck,
    errorPosition,
    children,
    onClickButton,
    buttonText,
    withSideButton,
    withClearButton,
    variant,
    options,
    onApply,
    onReset,
    onClearInput,
    onFocus,
    onBlur,
    autoFocus,
    searchBoxTitle,
    // infiniteScrollConfig,
    onClickOptions,
    onKeyDown,
    selectedOptions,
    value,
    max,
    min,
    "data-testid": dataTestId,
    icon,
    rightIcon,
    inputStyle,
    containerStyle,
    phoneNumberProps,
    ...rest
  } = props;

  const theme = useTheme();
  const { greaterThanMobile } = useBreakpoints();

  const countryOptions = useMemo(
    () =>
      variant === "phone_number"
        ? defaultCountries?.map((v) => {
            const parsedCountry = parseCountry(v);
            const isSelected = parsedCountry.iso2 === phoneNumberProps?.country.iso2;

            return {
              ...parsedCountry,
              placeholderLabel: <FlagImage size={16} iso2={parsedCountry.iso2} />,
              label: (
                <FlexContainer
                  alignItems="center"
                  justifyContent="space-between"
                  gap={8}
                  flexWrap={false}>
                  <FlexContainer alignItems="center" gap={8}>
                    <FlagImage size={16} iso2={parsedCountry.iso2} />
                    <TextBold
                      color={
                        isSelected ? theme.colors.primary.default : theme.colors.neutral.neutral90
                      }>
                      {parsedCountry.name}
                    </TextBold>
                    {isSelected && <Icon src={ChecklistIcon} size={16} />}
                  </FlexContainer>
                  <Text
                    color={
                      isSelected ? theme.colors.primary.default : theme.colors.neutral.neutral90
                    }>
                    +{parsedCountry.dialCode}
                  </Text>
                </FlexContainer>
              ),
            };
          })
        : [],
    [phoneNumberProps]
  );

  const inputRef = useRef<HTMLInputElement>();
  const {
    ref: searchResultRef,
    isComponentVisible: isShowSearchResult,
    setIsComponentVisible: setIsShowSearchResult,
  } = useOutsideClickHandler(false, [inputRef]);

  const [tempSelectedOptions, setTempSelectedOptions] = useState([]);

  const buttonClickHandler = (e: any) => {
    e.preventDefault();
    const event = {
      target: inputRef.current,
    };

    onClickButton?.(event);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    inputRef.current.value = "";
  };

  const onFocusHandler = () => {
    setIsShowSearchResult(true);
    onFocus();
  };

  const clickOptionsHandler = (v: any) => {
    if (onClickOptions) {
      onClickOptions(v);
      return;
    }

    if (onApply) {
      setTempSelectedOptions(toggleElementsById(v, tempSelectedOptions));
    }
  };

  const applyHandler = () => {
    onApply?.(tempSelectedOptions);
    setIsShowSearchResult(false);
  };

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "number" && max) {
      const newValue = parseInt(e.target.value) || 0;

      if (newValue <= max && newValue >= min) {
        onChange && onChange(e);
      }

      return;
    }

    onChange && onChange(e);
  };

  const onWheelHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "number") {
      e.target.blur();
    }
  };

  useEffect(() => {
    if (selectedOptions) {
      setTempSelectedOptions(selectedOptions);
    }
  }, [selectedOptions]);

  useEffect(() => {
    autoFocus && inputRef.current?.focus();
  }, []);

  const isNoMatchedKeyword =
    (options as any)?.filter((v: any) =>
      v?.name.toLowerCase().includes((value as any)?.toLowerCase())
    )?.length === 0;

  return (
    <ThemeProvider theme={{ variant }}>
      <Container
        width={width}
        height={height}
        className="text-field-container"
        style={containerStyle}>
        {prefixContent && prefixContent}
        {variant === "phone_number" && (
          <CountryCodeContainer>
            <Dropdown
              enableSearch
              showSelected
              padding="0"
              onClick={(idx) => phoneNumberProps?.setCountry(countryOptions[idx].iso2)}
              selectedIndex={defaultCountries?.findIndex(
                (v) => parseCountry(v).iso2 === phoneNumberProps?.country.iso2
              )}
              options={countryOptions}
              optionWrapperVariant={greaterThanMobile ? "default" : "drawer"}
              optionsWrapperStyle={{
                left: "-16px",
                top: "28px",
                width: inputRef?.current?.clientWidth,
              }}
            />
            <Divider variant="vertical" height="24px" color={theme.colors.neutral.neutral40} />
            <Text size={14}>+{phoneNumberProps?.country.dialCode}</Text>
          </CountryCodeContainer>
        )}
        <Wrapper className="text-field-wrapper">
          {variant === "currency" && (
            <FormatCurrency isDisabled={rest?.disabled} className="currency-sign">
              Rp.
            </FormatCurrency>
          )}
          {icon && icon}
          <InputStyle
            type={type}
            name={name}
            placeholder={placeholder}
            onChange={changeHandler}
            ref={mergeRefs([inputRef, ref])}
            error={error}
            spellCheck={spellCheck}
            autoComplete="off"
            withSideButton={withSideButton}
            onFocus={onFocusHandler}
            onWheel={onWheelHandler}
            value={value}
            style={inputStyle}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            {...rest}
            data-testid={dataTestId}
          />
          {rightIcon}
          {withSideButton && (
            <SideButton type="" onClick={(e: any) => buttonClickHandler(e)}>
              {buttonText}
            </SideButton>
          )}
          {withClearButton && <CrossIcon onClick={onClearInput} />}
        </Wrapper>
        {error && (
          <ErrorMessage errorPosition={errorPosition} data-testid={`${dataTestId}-error-message`}>
            {error}
          </ErrorMessage>
        )}
        {options && isShowSearchResult && (
          <SearchResultContainer
            ref={searchResultRef}
            className="search-result-container"
            isLarger={!(onApply || onReset)}>
            <TextBold color="neutral80" mb={10}>
              {searchBoxTitle}
            </TextBold>
            <SearchList isLarger={!(onApply || onReset)}>
              {(selectedOptions as any)?.map((v: any, idx: any) => (
                <Checkbox
                  key={idx}
                  my={14}
                  mx={12}
                  label={v?.name}
                  checked
                  onClick={() => clickOptionsHandler(v)}
                />
              ))}
              {(options as any)?.map((v: any, idx: any) => {
                return (
                  !checkAvailabilityById(selectedOptions, v?.id) &&
                  v?.name.toLowerCase().includes(value?.toLowerCase()) && (
                    <Checkbox
                      key={idx}
                      my={14}
                      mx={12}
                      label={v?.name}
                      onClick={() => clickOptionsHandler(v)}
                    />
                  )
                );
              })}
              {isNoMatchedKeyword && (
                <EmptyDataSmile
                  variantImage="people"
                  title="Mohon maaf kata kunci tidak ditemukan"
                  text="Coba ubah dengan kata kunci lain"
                />
              )}
            </SearchList>
            {(onApply || onReset) && (
              <ButtonWrapper>
                {onReset && (
                  <Button variant="primaryLight" size="m" mr={10} onClick={onReset}>
                    Reset
                  </Button>
                )}
                {onApply && (
                  <Button variant="primary" size="m" onClick={applyHandler}>
                    Apply
                  </Button>
                )}
              </ButtonWrapper>
            )}
          </SearchResultContainer>
        )}
      </Container>
    </ThemeProvider>
  );
});

TextField.defaultProps = {
  variant: "default",
  type: "text",
  width: null,
  height: null,
  name: "",
  placeholder: "",
  onChange: () => {},
  spellCheck: false,
  error: "",
  errorPosition: "left",
  search: false,
  searchPosition: "end",
  prefixContent: null,
  onClickButton: () => {},
  withSideButton: false,
  buttonText: "Add",
  options: null,
  onApply: null,
  onReset: null,
  onFocus: () => {},
  onBlur: () => {},
  searchBoxTitle: "[Your Title Here]",
  infiniteScrollConfig: null,
  onClickOptions: null,
  isMultiple: false,
  max: null,
  min: 0,
};

export default TextField;
